/** Plate-based V2 of the NDA editor — thin config wrapper around the shared
    PlateDocumentDrawer. NDA-specific: pulls `entityType` (constitution type) into
    the template metadata, which the NDA template substitutes. */
import { useCallback } from 'react';
import dayjs from 'dayjs';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { closeNDA, setNDAData } from '../../store/investor/action';
import { fetchNDATemplate, fetchOrgInfoById } from '../../store/investor/investorApiHelper';
import { fetchCompanyOverviewData } from '../../store/dashboard/overview/actions';
import { OverviewData } from '../../store/dashboard/overview/types';
import { initializeTemplateWithMetadata } from '../../utils/initializeTemplateWithMetadata';
import { DATE_FORMAT } from '../../utils/constants/commonConstants';
import { AICA_NDA_SAVE } from '../../utils/constants/mixpanelEvents/investorEvents';
import { PlateDocumentDrawer, type BuildInitialTemplateArgs } from '../PlateDocumentEditor/PlateDocumentDrawer';

function NDAv2() {
    const dispatch = useAppDispatch();
    const ndaData = useAppSelector((state) => state.investor.nda);
    const orgId = ndaData.orgId ?? '';
    const companyName = ndaData.companyName ?? '';

    const buildInitialTemplate = useCallback(
        ({ orgId, investorOrgId, emailId, companyName, companyMainImg, dispatch }: BuildInitialTemplateArgs) =>
            new Promise<any[]>((resolve, reject) => {
                dispatch(
                    fetchCompanyOverviewData({
                        assesseeOrgId: orgId,
                        investorOrgId,
                        onSuccess: async (data: OverviewData) => {
                            const [template, orgInfo] = await Promise.all([
                                fetchNDATemplate({ orgId, accountType: 'INVESTOR', pnoId: investorOrgId, primaryEmail: emailId }),
                                fetchOrgInfoById({ orgId }).catch(() => null),
                            ]);
                            resolve(
                                initializeTemplateWithMetadata(template || [], {
                                    pnoLogo: companyMainImg,
                                    companyName: companyName ?? '',
                                    companyAddress: data?.headquarterLocation?.fullAddress ?? '',
                                    entityType: orgInfo?.constitutionType ?? '',
                                    userName: '',
                                    userDesignation: '',
                                    userEmail: '',
                                    currentDate: dayjs().format(DATE_FORMAT),
                                }),
                            );
                        },
                        onError: () => reject(new Error('company overview fetch failed')),
                    }),
                );
            }),
        [],
    );

    return (
        <PlateDocumentDrawer
            documentType="NDA"
            title="NDA"
            fileNamePrefix="NDA"
            drawerId="nda-plate"
            toolbarHostId="nda-v2-toolbar-host"
            commentContainerId="nda-comment-container"
            uploadSource="nda"
            imageDocumentType="nda"
            mixpanelSaveEvent={AICA_NDA_SAVE}
            orgId={orgId}
            companyName={companyName}
            loading={!!ndaData.loading}
            onClose={() => dispatch(closeNDA())}
            setLoading={(loading) => dispatch(setNDAData({ loading }))}
            buildInitialTemplate={buildInitialTemplate}
        />
    );
}

export default NDAv2;
